/**
 * MODULE: _shared/frontend-shell/src/features/crm/utils/crmActions.ts
 * PURPOSE: Single CRM action map factory — every CTA resolves through `createCrmActions` + `useCrmActions` (no silent empty handlers)
 * DEPENDENCIES: ../context/CrmContext (CrmContextValue type), ../types/*, ../hooks/useOpportunity (CRM_NEW_OPPORTUNITY_ID)
 * PUBLIC_API: createCrmActions, CrmActionsMap, CrmExportTabular, CrmExportRow, CrmExportCell
 * SIDE_EFFECTS: Invokes CrmContext openers; optimistic patches; client-side CSV or XLSX download (XLSX via dynamic import)
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 1083/1150
 * RECENT: 2026-06-22 [2.2.1470] — **`goAccountsIndex`** navigates to **`/crm/accounts`** (canonical My accounts tab) — View Current Accounts sprint.
 * RECENT_PRIOR: 2026-06-21 [2.2.1460] — **`saveOpportunity`**
 * RECENT_PRIOR: 2026-05-26 [2.2.1226] — `restoreTerritoryAccount` uses `rollback.restoreAccount`; `deleteRecord` calls `deleteTerritoryAccount`; remove invalid `archiveTerritoryAccount` from actions map.
 * RECENT_PRIOR: 2026-05-11 Task 6 #87 — **`viewRecord('account', …)`** opens **`openAccountDrawer`** for id or **`/companies/`** or **`/crm/accounts/`** paths; other paths still **`location.assign`**
 * RECENT_PRIOR: 2026-05-11 [2.2.943] — Hub pages own CSV export **`showToast`** (success or empty); **`exportRecordsToFile`** keeps async **xlsx** success toast only; hub **`handleExport*Xlsx`** empty guard matches CSV — root **`CHANGELOG` [2.2.943]`**
 * RECENT_PRIOR: 2026-05-11 — **`runPipelineGeneration`** pipeline-run task due **17:00** on **`scheduleStartDate`** (aligns with **`defaultDueDateTime()`**). [2.2.942] CRM finalisation — `saveActivity` **`try/catch`** + **`console.error('[crm] saveActivity persistence failed', …)`** then re-throw — root **`CHANGELOG` [2.2.942]`**
 * RECENT_PRIOR: 2026-05-08 [2.2.890] Sequential bulk PATCH JSDoc (outcome counts); docs sync — root **`CHANGELOG` [2.2.890]`**
 * RECENT_PRIOR: 2026-05-08 [2.2.889] **`bulkPersistOpportunityPatchesSequential`** — Deals bulk advance (partial success) — root **`CHANGELOG` [2.2.889]`**
 * RECENT_PRIOR: 2026-05-08 [2.2.876] CRM persistence UX compliance: persisted CRM create, edit, task, activity, deal, account, related-entity, and pipeline run-history flows; stale demo copy removed — root **`CHANGELOG` [2.2.876]`**
 * RECENT_PRIOR: 2026-05-08 [2.2.873] **`saveActivity`** **`await`**s **`ctx.addActivity`** (CRM API persist) — root **`CHANGELOG` [2.2.873]`**
 * RECENT_PRIOR: 2026-05-07 [2.2.861] **`logCall`** **`metadata.recordingUrl`** when **`LogCallPayload.recordingUrl`** set — root **`CHANGELOG` [2.2.861]`**
 * RECENT_PRIOR: 2026-05-07 [2.2.848] **`logCall`** accepts **`LogCallPayload`** (**`../types/logCallPayload.types`**) to **`saveActivity`** — root **`CHANGELOG` [2.2.848]`**
 * RECENT_PRIOR: 2026-05-03 [2.2.791] Removed useless try or catch rethrows; dead **`eslint-disable`** cleanup — CHANGELOG [2.2.791]
 * RECENT_PRIOR: 2026-05-01 [2.2.780] **`openCreateAccount`** → **`openAccountDrawer(CRM_NEW_ACCOUNT_ID)`** — CHANGELOG [2.2.780]
 * RECENT_PRIOR: 2026-05-01 [2.2.762] **`composeEmail`**, **`scheduleMeeting`**, **`logCall`**, activity **`saveSchedule`**, **`logActivity`** **`persistWithoutModal`** — CHANGELOG [2.2.762]
 * RECENT_PRIOR: 2026-04-30 [2.2.750] **`bulkAssign`**, **`bulkTag`**, **`bulkDelete`**, **`bulkMoveStage`** — CHANGELOG [2.2.750]. [2.2.748] Optional **`CrmActionsRouter`** — CHANGELOG [2.2.748]
 * RECENT_PRIOR: 2026-04-30 [2.2.738] **`exportRecordsToFile`** xlsx + **`deleteActivity`** — CHANGELOG [2.2.738]
 * RECENT_PRIOR: 2026-04-30 [2.2.735] Docs sync — CHANGELOG [2.2.735]
 * RECENT_PRIOR: 2026-04-30 [2.2.734] **`runPipelineGeneration`** opens **`openPipelineModal`** or executes payload via **`saveSchedule`** — CHANGELOG [2.2.734]
 * RECENT_PRIOR: 2026-04-30 [2.2.733] MODULE RECENT line: plain prose (no nested markdown emphasis) for Vitest or esbuild parse; Vitest createCrmActions in utils or __tests__ — CHANGELOG [2.2.733]
 */

import { createElement } from 'react';
import type { LogCallPayload } from '../types/logCallPayload.types';
import type { ActivityModalOpenPrefill } from '../types/crm.types';
import type { CrmApiBridgeValue, CrmContextValue, CrmRollbackValue } from '../context/CrmContext';
import { applyFiltersToSearchParams, crmUrlFilterProfileFromPathname } from './crmFilterUrl';
import { CRM_NEW_CONTACT_ID } from '../hooks/useContact';
import { CRM_NEW_OPPORTUNITY_ID } from '../hooks/useOpportunity';
import type { Activity, ActivityType } from '../types/activity.types';
import type { CrmFilterState, DrilldownConfig } from '../types/crm.types';
import type { Opportunity, OpportunityStage } from '../types/opportunity.types';
import type { PipelineGenerationPayload, RunPipelineGenerationFn } from '../types/pipelineGeneration.types';
import type { Task } from '../types/task.types';
import { MOCK_SCHEDULE_OWNERS } from '../data/scheduleModalCatalog';
import { OPPORTUNITY_TERRITORY_OPTIONS } from '../data/opportunityDrawerOptions';
import { resolveTerritoryLabel } from '../hooks/useCrmTerritoryOptions';
import { buildCrmContactDetailPath, buildCrmDealDetailPath } from '../../../routes/builders';
import { formatCrmDate } from './crmFormatters';

/** Tabular export payload for CSV or XLSX (pages pass filtered rows; omit for legacy deals export from context). */
export type CrmExportCell = string | number | boolean | null | undefined;
export type CrmExportRow = CrmExportCell[];
export type CrmExportTabular = {
  sheetName: string;
  /** File stem without extension; timestamp appended to avoid collisions. */
  filenameBase?: string;
  headers: string[];
  rows: CrmExportRow[];
};

/** Optional success or error notifications for tabular export (wired from `useToast` in `CrmActionsShellProvider`). */
export type CrmExportToast = {
  success: (message: string) => void;
  error: (message: string) => void;
};

function assertCrmExportFormat(format: string): asserts format is 'csv' | 'xlsx' {
  if (format !== 'csv' && format !== 'xlsx') {
    throw new Error(`Invalid CRM export format: ${format}`);
  }
}

/** Logs only in development when a CRM shell action is still a client stub. */
function warnCrmShellStub(message: string, meta?: Record<string, unknown>): void {
  if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
    console.warn(`[crm] ${message}`, meta);
  }
}

/** Excel worksheet name: max 31 chars; forbidden characters replaced. */
function sanitizeSheetName(name: string): string {
  const cleaned = name.replace(/[[\]:*?/\\]/g, '-').trim();
  const base = cleaned.length > 0 ? cleaned : 'Sheet1';
  return base.length > 31 ? base.slice(0, 31) : base;
}

function csvEscapeCell(v: unknown): string {
  return `"${String(v).replace(/"/g, '""')}"`;
}

function buildCsvBlobContent(headers: string[], rows: CrmExportRow[]): string {
  const lines = [
    headers.map(csvEscapeCell).join(','),
    ...rows.map((row) => row.map(csvEscapeCell).join(',')),
  ];
  return lines.join('\n');
}

function triggerCsvDownload(csvContent: string, downloadName: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  if (typeof document === 'undefined') return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = downloadName;
  a.click();
  URL.revokeObjectURL(url);
}

type XlsxModule = typeof import('xlsx');

function resolveXlsxNamespace(mod: XlsxModule & { default?: XlsxModule }): XlsxModule {
  const ns = mod as Record<string, unknown>;
  // Prefer namespace shape (typical ESM `import('xlsx')`); avoid reading `.default` when unset — Vitest strict mocks throw.
  if (typeof ns.writeFile === 'function') {
    return mod as XlsxModule;
  }
  try {
    const d = ns.default;
    if (d && typeof d === 'object' && typeof (d as XlsxModule).writeFile === 'function') {
      return d as XlsxModule;
    }
  } catch {
    /* ignore missing default on partial mocks */
  }
  return mod as XlsxModule;
}

/** Auto-fit column widths from sheet grid via sheet_to_json (per product spec). */
function applyAutoColWidthsFromSheet(ws: import('xlsx').WorkSheet, XLSX: XlsxModule): void {
  const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as unknown[][];
  if (!aoa.length) return;
  const colCount = Math.max(...aoa.map((row) => (Array.isArray(row) ? row.length : 0)));
  const cols: import('xlsx').ColInfo[] = [];
  for (let c = 0; c < colCount; c++) {
    let maxLen = 10;
    for (const row of aoa) {
      if (!Array.isArray(row)) continue;
      const len = String(row[c] ?? '').length;
      if (len > maxLen) maxLen = len;
    }
    cols.push({ wch: Math.min(maxLen + 2, 60) });
  }
  ws['!cols'] = cols;
}

/** Entity discriminator for bulk CRM list operations (contacts hub mock list, deals pipeline). */
export type CrmEntityType = 'contact' | 'deal';

function stubLog(action: string, detail?: string): void {
  if (import.meta.env.DEV) {
    console.info(`[crmActions] ${action}`, detail ?? '');
  }
}

const SHELL_FILTER_KEYS: (keyof CrmFilterState)[] = [
  'search',
  'owner',
  'dateRange',
  'status',
  'stage',
  'territory',
  'sort',
  'region',
];

/** Passed from `CrmActionsProvider` so `applyFilters` and `resetFilters` sync the URL (replace). */
export type CrmActionsRouter = {
  navigate: (to: { pathname: string; search?: string } | string, opts?: { replace?: boolean }) => void;
  pathname: string;
};

/** Non-silent stub when read-only InlineEditableField should never persist. */
function warnReadOnlySaveAttempt(label: string): void {
  warnCrmShellStub('read-only CRM field cannot be saved', { label });
}

/** Maps shell CRM context into the shared action surface (centralized CTA handlers). */
export function createCrmActions(
  ctx: CrmContextValue,
  api: CrmApiBridgeValue,
  rollback: CrmRollbackValue,
  router?: CrmActionsRouter,
  toast?: CrmExportToast
): CrmActionsMap {
  function contactDisplayName(c: { first_name: string; last_name: string; email?: string }): string {
    const n = `${c.first_name} ${c.last_name}`.trim();
    return n || (c.email ?? '').trim() || 'Contact';
  }

  function activityTypeLabel(type: ActivityType): string {
    return type.charAt(0).toUpperCase() + type.slice(1);
  }

  function navigateToDealDetail(id: string): boolean {
    if (!router?.navigate) return false;
    const { pathname } = router;
    if (pathname.startsWith('/crm')) {
      router.navigate(buildCrmDealDetailPath(id));
      return true;
    }
    if (/^\/accounts\/[^/]+/.test(pathname)) {
      const params =
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search)
          : new URLSearchParams();
      params.set('tab', 'opportunities');
      params.set('deal', id);
      router.navigate({ pathname, search: `?${params.toString()}` });
      return true;
    }
    return false;
  }

  const openOpportunityDrawer = (id: string) => {
    stubLog('openOpportunityDrawer', id);
    if (navigateToDealDetail(id)) return;
    ctx.openOpportunityDrawer(id);
  };

  const openCreateDeal = () => {
    stubLog('openCreateDeal', CRM_NEW_OPPORTUNITY_ID);
    ctx.openOpportunityDrawer(CRM_NEW_OPPORTUNITY_ID);
  };

  const openCreateContact = (prefill?: Record<string, string>) => {
    stubLog('openCreateContact', JSON.stringify(prefill ?? {}));
    ctx.openContactDrawer(CRM_NEW_CONTACT_ID, prefill);
  };

  const openCreateAccount = () => {
    stubLog('openCreateAccount');
    ctx.openCreateAccountModal();
  };

  const openCreateActivity = (prefill?: Partial<Activity>) => {
    stubLog('openCreateActivity', JSON.stringify(prefill ?? {}));
    ctx.openActivityModal(prefill);
  };

  const openScheduleFollowUp = (entityId?: string, entityType: Task['relatedEntityType'] = 'contact') => {
    stubLog('openScheduleFollowUp', `${entityType}:${entityId ?? ''}`);
    if (entityId) {
      ctx.openScheduleModal(
        {
          relatedEntityId: entityId,
          relatedEntityType: entityType,
          title: 'Follow-up',
        },
        { mode: 'task' }
      );
      return;
    }
    ctx.openScheduleModal({ title: 'Follow-up', type: 'follow_up' }, { mode: 'task' });
  };

  const applyFilters = (patch: Record<string, unknown>) => {
    stubLog('applyFilters', JSON.stringify(patch));
    if (!router) {
      warnCrmShellStub('CRM applyFilters skipped — no router', { patch });
      return;
    }
    const profile = crmUrlFilterProfileFromPathname(router.pathname);
    const next: CrmFilterState = { search: '' };
    for (const [rawKey, rawVal] of Object.entries(patch)) {
      const key = rawKey as keyof CrmFilterState;
      if (!SHELL_FILTER_KEYS.includes(key)) {
        warnCrmShellStub('CRM applyFilters ignored unknown key', { key: rawKey, value: rawVal });
        continue;
      }
      if (key === 'dateRange' && rawVal != null && typeof rawVal === 'object') {
        next.dateRange = rawVal as { from: string; to: string };
        continue;
      }
      const s = rawVal == null ? '' : String(rawVal);
      if (key === 'search') {
        next.search = s;
      } else if (s) {
        (next as unknown as Record<string, string>)[key] = s;
      }
    }
    const sp = applyFiltersToSearchParams(profile, next);
    const qs = sp.toString();
    router.navigate({ pathname: router.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
  };

  const resetFilters = () => {
    stubLog('resetFilters');
    if (!router) {
      warnCrmShellStub('CRM resetFilters skipped — no router');
      return;
    }
    router.navigate({ pathname: router.pathname, search: '' }, { replace: true });
  };

  const exportRecordsToFile = (format: string, tabular?: CrmExportTabular): void => {
    assertCrmExportFormat(format);
    stubLog('exportRecords', format);
    const bundle: CrmExportTabular =
      tabular ??
      ({
        sheetName: 'Deals',
        headers: ['Name', 'Account', 'Stage', 'Amount', 'Close Date', 'Owner'],
        rows: ctx.opportunities.map((o) => [
          o.name,
          o.accountName,
          o.stage,
          o.amount,
          o.closeDateISO,
          o.ownerName,
        ]),
      } satisfies CrmExportTabular);

    const rows = bundle.rows;
    if (!rows || rows.length === 0) {
      return;
    }

    const fileStem =
      bundle.filenameBase != null && bundle.filenameBase.trim() !== ''
        ? `${bundle.filenameBase.trim()}-${Date.now()}`
        : `crm-export-${Date.now()}`;

    if (format === 'csv') {
      const csvContent = buildCsvBlobContent(bundle.headers, bundle.rows);
      const filename = `${fileStem}.csv`;
      triggerCsvDownload(csvContent, filename);
      return;
    }

    // xlsx: dynamic import keeps initial bundle smaller (xlsx entry exceeds ~200KB gzipped).
    void (async () => {
      const raw = (await import('xlsx')) as XlsxModule & { default?: XlsxModule };
      const XLSX = resolveXlsxNamespace(raw);
      const aoa: CrmExportRow[] = [bundle.headers as CrmExportRow, ...bundle.rows];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      applyAutoColWidthsFromSheet(ws, XLSX);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(bundle.sheetName));
      const filename = `${fileStem}.xlsx`;
      XLSX.writeFile(wb, filename);
      toast?.success(`Exported ${rows.length} record${rows.length !== 1 ? 's' : ''} — ${filename}`);
    })();
  };

  const exportRecordsFromEntity = (entityType: string, filters: CrmFilterState) => {
    stubLog('exportRecords', JSON.stringify({ entityType, filters }));
    exportRecordsToFile('csv');
  };

  /** Overload: `exportRecords('csv' | 'xlsx')` or legacy `(entityType, filters)`. */
  function exportRecords(
    formatOrEntity: 'csv' | 'xlsx' | string,
    filters?: CrmFilterState
  ): void {
    if (filters !== undefined) {
      exportRecordsFromEntity(formatOrEntity, filters);
      return;
    }
    if (formatOrEntity === 'csv' || formatOrEntity === 'xlsx') {
      exportRecordsToFile(formatOrEntity);
      return;
    }
    warnCrmShellStub('exportRecords invalid first argument', { formatOrEntity });
  }

  const deleteDeal = async (id: string): Promise<void> => {
    const deal = (ctx.crmOpportunities ?? []).find((o) => o.id === id);
    const name = deal?.name?.trim() || 'this deal';
    const ok = await ctx.confirmDestructive({
      title: 'Delete deal?',
      message: createElement(
        'span',
        { className: 'text-sm text-gray-700 dark:text-gray-300' },
        'Delete ',
        createElement('strong', null, name),
        '? This cannot be undone.'
      ),
      confirmLabel: 'Delete deal',
    });
    if (!ok) return;
    stubLog('deleteDeal', id);
    ctx.removeOpportunity(id);
    try {
      await api.deals.deleteRemote(id);
    } catch (e) {
      rollback.restoreOpportunity(id);
      throw e;
    }
  };

  const deleteCrmListContact = async (
    contactId: string,
    opts?: { onCommitFailed?: (err: unknown) => void }
  ): Promise<{ cancelCommit: () => void; restoreContact: () => void } | null> => {
    const row = ctx.crmContacts.find((c) => c.id === contactId);
    const display = row ? contactDisplayName(row) : 'this contact';
    const ok = await ctx.confirmDestructive({
      title: 'Delete contact?',
      message: createElement(
        'span',
        { className: 'text-sm text-gray-700 dark:text-gray-300' },
        'Delete ',
        createElement('strong', null, display),
        '? This cannot be undone.'
      ),
      confirmLabel: 'Delete contact',
    });
    if (!ok) return null;
    stubLog('deleteCrmListContact', contactId);
    api.contacts.removeContact(contactId);
    const cancelCommit = api.contacts.scheduleContactDeleteCommit(contactId, {
      onCommitted: () => {},
      onCommitFailed: (err) => opts?.onCommitFailed?.(err),
    });
    const undoDelete = () => {
      api.contacts.cancelContactDelete(contactId);
    };
    return {
      cancelCommit,
      /** Same as `cancelCommit` — clears timer and restores the row (kept for callers that still call both). */
      restoreContact: undoDelete,
    };
  };

  const deleteTerritoryAccount = async (accountId: string): Promise<void> => {
    const ok = await ctx.confirmDestructive({
      title: 'Archive account?',
      message:
        'This account will be hidden from default lists. Deals and contacts stay linked — you can restore the account from the Archived view.',
      confirmLabel: 'Archive account',
    });
    if (!ok) return;
    stubLog('archiveTerritoryAccount', accountId);
    ctx.removeAccount(accountId);
    try {
      await api.accounts.deleteAccount(accountId);
    } catch (e) {
      rollback.restoreAccount(accountId);
      throw e;
    }
  };

  const restoreTerritoryAccount = async (accountId: string): Promise<void> => {
    stubLog('restoreTerritoryAccount', accountId);
    await api.accounts.restoreAccount(accountId);
    rollback.restoreAccount(accountId);
    await ctx.refetchAllCrmLists();
  };

  const deleteRecord = async (type: string, id: string) => {
    stubLog('deleteRecord', `${type}:${id}`);
    const t = type.toLowerCase();
    if (t === 'deal' || t === 'opportunity') return deleteDeal(id);
    if (t === 'contact') {
      await deleteCrmListContact(id);
      return;
    }
    if (t === 'account') return deleteTerritoryAccount(id);
    warnCrmShellStub('deleteRecord unsupported type', { type, id });
  };

  const editDeal = (id: string) => {
    stubLog('editDeal', id);
    openOpportunityDrawer(id);
  };

  const editCrmListContact = (contactId: string) => {
    stubLog('editCrmListContact', contactId);
    if (router?.pathname?.startsWith('/crm')) {
      router.navigate(buildCrmContactDetailPath(contactId));
      return;
    }
    ctx.openContactDrawer(contactId);
  };

  const editTerritoryAccount = (accountId: string) => {
    stubLog('editTerritoryAccount', accountId);
    ctx.openAccountDrawer(accountId);
  };

  const editRecord = (type: string, id: string) => {
    stubLog('editRecord', `${type}:${id}`);
    const t = type.toLowerCase();
    if (t === 'deal' || t === 'opportunity') {
      editDeal(id);
      return;
    }
    if (t === 'contact') {
      editCrmListContact(id);
      return;
    }
    if (t === 'account') {
      editTerritoryAccount(id);
      return;
    }
    warnCrmShellStub('editRecord unsupported type', { type, id });
  };

  const viewRecord = (type: string, id: string) => {
    stubLog('viewRecord', `${type}:${id}`);
    const t = type.toLowerCase();
    if (t === 'deal' || t === 'opportunity') {
      openOpportunityDrawer(id);
      return;
    }
    if (t === 'account') {
      const raw = id.trim();
      const companiesMatch = raw.match(/^\/companies\/([^/?#]+)/);
      const crmAccountsMatch = raw.match(/^\/crm\/accounts\/([^/?#]+)/);
      let accountId: string | null = null;
      if (companiesMatch?.[1]) {
        accountId = decodeURIComponent(companiesMatch[1]);
      } else if (crmAccountsMatch?.[1]) {
        accountId = decodeURIComponent(crmAccountsMatch[1]);
      } else if (raw.startsWith('/')) {
        if (typeof window !== 'undefined') window.location.assign(raw);
        return;
      } else {
        accountId = raw;
      }
      if (accountId) ctx.openAccountDrawer(accountId);
      return;
    }
    if (t === 'contact') {
      if (router?.pathname?.startsWith('/crm')) {
        router.navigate(buildCrmContactDetailPath(id));
        return;
      }
      ctx.openContactDrawer(id);
      return;
    }
    warnCrmShellStub('viewRecord unsupported type', { type, id });
  };

  const persistOpportunityPatch = async (patch: Partial<Opportunity>) => {
    stubLog('persistOpportunityPatch', JSON.stringify(patch));
    await api.deals.persistOpportunityPatch(patch);
  };

  const readOnlyFieldSaveAttempt =
    (fieldLabel: string) => async (_value: string | number) => {
      warnReadOnlySaveAttempt(fieldLabel);
      throw new Error(`${fieldLabel} is read-only — it updates automatically when related values change.`);
    };

  const createTask = (prefill?: Partial<Task>) => {
    stubLog('createTask', JSON.stringify(prefill ?? {}));
    ctx.openScheduleModal(prefill, { mode: 'task' });
  };

  const editTask = (id: string) => {
    stubLog('editTask', id);
    ctx.openScheduleModal({ id } as Partial<Task>, { mode: 'task', editMode: true });
  };

  const deleteTask = async (id: string): Promise<boolean> => {
    const ok = await ctx.confirmDestructive({
      title: 'Delete task?',
      message: 'This task will be removed. This cannot be undone.',
      confirmLabel: 'Delete task',
    });
    if (!ok) return false;
    stubLog('deleteTask', id);
    ctx.removeTask(id);
    return true;
  };

  const completeTask = (id: string) => {
    stubLog('completeTask', id);
    ctx.patchTask(id, { status: 'done' });
  };

  const logActivity = (prefill?: ActivityModalOpenPrefill) => {
    stubLog('logActivity', JSON.stringify(prefill ?? {}));
    if (prefill?.persistWithoutModal) {
      const ownerRow = MOCK_SCHEDULE_OWNERS.find((o) => o.id === prefill.ownerId) ?? MOCK_SCHEDULE_OWNERS[0]!;
      const now = new Date().toISOString();
      void saveActivity({
        type: (prefill.type ?? 'email') as Activity['type'],
        subject: (prefill.subject ?? '').trim() || '(no subject)',
        relatedEntityId: prefill.relatedEntityId ?? 'unknown',
        relatedEntityType: (prefill.relatedEntityType ?? 'contact') as Activity['relatedEntityType'],
        relatedEntityName: prefill.relatedEntityName?.trim() || 'Contact',
        ownerId: prefill.ownerId ?? ownerRow.id,
        ownerName: prefill.ownerName ?? ownerRow.name,
        dueAtISO: prefill.dueAtISO ?? now,
        status: prefill.status ?? 'complete',
        notes: prefill.notes,
      });
      return;
    }
    ctx.openActivityModal(prefill);
  };

  const composeEmail = (contactId: string, prefill?: { subject?: string; body?: string }) => {
    stubLog('composeEmail', contactId);
    ctx.openEmailComposeModal(contactId, prefill);
  };

  const scheduleMeeting = (entityType: CrmEntityType, entityId: string) => {
    stubLog('scheduleMeeting', `${entityType}:${entityId}`);
    if (entityType === 'deal') {
      const opp = ctx.crmOpportunities.find((o) => o.id === entityId);
      const title = opp?.name?.trim() ? `Review: ${opp.name.trim()}` : 'Review: Deal';
      ctx.openScheduleModal(
        {
          type: 'meeting',
          subject: title,
          relatedEntityId: entityId,
          relatedEntityType: 'deal',
          relatedEntityName: opp?.name ?? 'Deal',
          status: 'scheduled',
        },
        { mode: 'activity' }
      );
      return;
    }
    const row = ctx.crmContacts.find((c) => c.id === entityId);
    const name = row ? contactDisplayName(row) : 'Contact';
    ctx.openScheduleModal(
      {
        type: 'meeting',
        subject: `Meeting with ${name}`,
        relatedEntityId: entityId,
        relatedEntityType: 'contact',
        relatedEntityName: name,
        status: 'scheduled',
      },
      { mode: 'activity' }
    );
  };

  const createDeal = (prefill?: Partial<Opportunity>) => {
    stubLog('createDeal', JSON.stringify(prefill ?? {}));
    const title = (prefill?.name ?? '').trim() || 'New deal';
    ctx.openScheduleModal(
      { ...(prefill as Partial<Task>), title, relatedEntityType: 'deal' },
      { mode: 'task' }
    );
  };

  const addContact = (prefill?: Record<string, string>) => {
    openCreateContact(prefill);
  };

  const scheduleFollowUp = (entityId: string, entityType: string) => {
    stubLog('scheduleFollowUp', `${entityType}:${entityId}`);
    ctx.openScheduleModal(
      {
        relatedEntityId: entityId,
        relatedEntityType: entityType as Task['relatedEntityType'],
        title: 'Follow-up',
      },
      { mode: 'task' }
    );
  };

  const openDrilldown = (config: DrilldownConfig) => {
    stubLog('openDrilldown', config.title);
    ctx.openDrilldown(config);
  };

  const saveSchedule = async (data: Partial<Task> & Partial<Activity>) => {
    stubLog('saveSchedule', JSON.stringify(data));
    const row = data as Partial<Task>;
    const act = data as Partial<Activity>;
    const activityKinds: ActivityType[] = ['call', 'email', 'meeting', 'note'];
    if (
      typeof act.subject === 'string' &&
      act.subject.trim() &&
      typeof act.dueAtISO === 'string' &&
      act.type &&
      activityKinds.includes(act.type as ActivityType)
    ) {
      const ownerRow = MOCK_SCHEDULE_OWNERS.find((o) => o.id === act.ownerId) ?? MOCK_SCHEDULE_OWNERS[0]!;
      await saveActivity({
        type: act.type as ActivityType,
        subject: act.subject.trim(),
        relatedEntityId: act.relatedEntityId ?? 'unknown',
        relatedEntityType: (act.relatedEntityType ?? 'deal') as Activity['relatedEntityType'],
        relatedEntityName: act.relatedEntityName?.trim() || act.relatedEntityId || 'Related',
        ownerId: act.ownerId ?? ownerRow.id,
        ownerName: act.ownerName ?? ownerRow.name,
        dueAtISO: act.dueAtISO,
        status: act.status ?? 'scheduled',
        notes: act.notes,
      });
      return;
    }
    if (typeof row.title !== 'string' || !row.title.trim() || !row.dueDateISO) {
      warnCrmShellStub('invalid task schedule save', { data });
      return;
    }
    if (row.id) {
      const { id, ...rest } = row;
      const patch: Partial<Task> = { ...rest };
      delete (patch as { id?: string }).id;
      await api.tasks.patchTask(id, patch);
      return;
    }
    await api.tasks.addTask({
      title: row.title.trim(),
      type: (row.type ?? 'task') as Task['type'],
      status: (row.status ?? 'todo') as Task['status'],
      priority: (row.priority ?? 'medium') as Task['priority'],
      ownerId: row.ownerId ?? 'user-1',
      ownerName: row.ownerName ?? 'Morgan Lee',
      dueDateISO: row.dueDateISO,
      description: row.description,
      notes: row.notes,
      relatedEntityId: row.relatedEntityId,
      relatedEntityType: row.relatedEntityType,
      relatedEntityName: row.relatedEntityName,
    });
  };

  const runPipelineGeneration: RunPipelineGenerationFn = ((payload?: PipelineGenerationPayload) => {
    if (payload === undefined) {
      stubLog('runPipelineGeneration:openModal');
      ctx.openPipelineModal();
      return;
    }
    stubLog('runPipelineGeneration:execute', JSON.stringify(payload));
    const territoryLabel = resolveTerritoryLabel(payload.territoryId, OPPORTUNITY_TERRITORY_OPTIONS);
    const ownerRow =
      MOCK_SCHEDULE_OWNERS.find((o) => o.id === payload.assignOwnerId) ?? MOCK_SCHEDULE_OWNERS[0]!;
    const due = new Date(`${payload.scheduleStartDate}T17:00:00`);
    const detailLines = [
      `segment=${payload.segmentType}`,
      `outreach=${payload.outreachType}`,
      `limit=${payload.limit}`,
      `dealMin=${payload.dealValueMin} dealMax=${payload.dealValueMax}`,
      payload.industries.length ? `industries=${payload.industries.join(';')}` : '',
      payload.notes?.trim() ? `instructions=${payload.notes.trim()}` : '',
    ].filter(Boolean);
    return saveSchedule({
      title: 'Pipeline generation run',
      type: 'task',
      status: 'todo',
      priority: payload.priority,
      ownerId: ownerRow.id,
      ownerName: ownerRow.name,
      dueDateISO: due.toISOString(),
      relatedEntityType: 'territory',
      relatedEntityId: payload.territoryId,
      relatedEntityName: territoryLabel,
      notes: detailLines.join('\n'),
    } as Parameters<typeof saveSchedule>[0]);
  }) as RunPipelineGenerationFn;

  const saveActivity = async (data: Partial<Activity>) => {
    stubLog('saveActivity', JSON.stringify(data));
    // try/catch surfaces silent persistence failures via devtools — there is at
    // least one fire-and-forget caller (`void saveActivity(...)` in
    // `openCreateActivity` for the `persistWithoutModal` path) that would
    // otherwise swallow a failed POST /api/v1/crm/activities. Re-throw preserves
    // the existing contract for awaiting callers (e.g. ActivityModal `onSave`).
    try {
      if (data.id) {
        await ctx.patchActivity(data.id, data);
      } else {
        await ctx.addActivity({
          ...data,
          id: crypto.randomUUID(),
          status: data.status ?? 'open',
        } as Omit<Activity, 'id' | 'createdAtISO' | 'updatedAtISO'> & { id?: string });
      }
    } catch (err) {
      console.error('[crm] saveActivity persistence failed', {
        operation: data.id ? 'patch' : 'create',
        activityId: data.id,
        relatedEntityId: data.relatedEntityId,
        error: err,
      });
      throw err;
    }
  };

  const logCall = ((arg1: CrmEntityType | LogCallPayload, entityId?: string) => {
    if (typeof arg1 === 'object' && arg1 !== null && 'contactId' in arg1) {
      const p = arg1 as LogCallPayload;
      stubLog('logCall', `persist:${p.contactId}`);
      const row = ctx.crmContacts.find((c) => c.id === p.contactId);
      const name = row ? contactDisplayName(row) : 'Contact';
      const outcomeLabel: Record<LogCallPayload['outcome'], string> = {
        'follow-up-required': 'Follow-up required',
        completed: 'Completed',
        'no-answer': 'No answer',
        'left-voicemail': 'Left voicemail',
      };
      const ownerRow = MOCK_SCHEDULE_OWNERS[0]!;
      return saveActivity({
        type: 'call',
        subject: 'Call logged',
        relatedEntityId: p.contactId,
        relatedEntityType: 'contact',
        relatedEntityName: name,
        ownerId: ownerRow.id,
        ownerName: ownerRow.name,
        dueAtISO: new Date().toISOString(),
        status: 'complete',
        notes: p.summary.trim(),
        outcome: outcomeLabel[p.outcome],
        metadata: {
          outcome: p.outcome,
          durationSeconds: p.durationSeconds,
          transcript: p.transcript,
          ...(p.recordingUrl?.trim() ? { recordingUrl: p.recordingUrl.trim() } : {}),
        },
      });
    }
    const entityType = arg1;
    const id = entityId ?? '';
    stubLog('logCall', `${entityType}:${id}`);
    if (entityType === 'deal') {
      const opp = ctx.crmOpportunities.find((o) => o.id === id);
      ctx.openScheduleModal(
        {
          type: 'call',
          subject: opp?.name?.trim() ? `Call — ${opp.name.trim()}` : 'Call',
          relatedEntityId: id,
          relatedEntityType: 'deal',
          relatedEntityName: opp?.name ?? 'Deal',
          status: 'complete',
        },
        { mode: 'activity' }
      );
      return;
    }
    const row = ctx.crmContacts.find((c) => c.id === id);
    const name = row ? contactDisplayName(row) : 'Contact';
    ctx.openScheduleModal(
      {
        type: 'call',
        subject: `Call with ${name}`,
        relatedEntityId: id,
        relatedEntityType: 'contact',
        relatedEntityName: name,
        status: 'complete',
      },
      { mode: 'activity' }
    );
  }) as CrmActionsMap['logCall'];

  const saveOpportunity = async (data: Partial<Opportunity>): Promise<Opportunity | undefined> => {
    stubLog('saveOpportunity', JSON.stringify(data));
    const saved = await api.deals.saveOpportunity(data);
    return saved;
  };

  const markActivityComplete = async (activityId: string) => {
    stubLog('markActivityComplete', activityId);
    ctx.patchActivity(activityId, { status: 'complete' });
  };

  const deleteActivity = async (id: string): Promise<void> => {
    const act = ctx.crmActivities.find((a) => a.id === id);
    const typeLbl = act ? activityTypeLabel(act.type) : 'activity';
    const datePart = act?.dueAtISO ? formatCrmDate(act.dueAtISO) : '';
    const ok = await ctx.confirmDestructive({
      title: 'Delete activity?',
      message: createElement(
        'span',
        { className: 'text-sm text-gray-700 dark:text-gray-300' },
        'Delete this ',
        createElement('strong', null, typeLbl),
        ' activity',
        datePart ? ` (${datePart})` : '',
        '? This cannot be undone.'
      ),
      confirmLabel: 'Delete activity',
    });
    if (!ok) return;
    stubLog('deleteActivity', id);
    ctx.removeActivity(id);
    try {
      await api.activities.deleteActivity(id);
    } catch (e) {
      rollback.restoreActivity(id);
      throw e;
    }
  };

  const goAccountsIndex = () => {
    stubLog('goAccountsIndex');
    if (typeof window !== 'undefined') {
      window.location.assign('/crm/accounts');
    }
  };

  const bulkAssign = async (ids: string[], ownerId: string): Promise<void> => {
    stubLog('bulkAssign', `${ids.length}:${ownerId}`);
    const uniq = [...new Set(ids)];
    const snapshot: Record<string, { ownerId?: string; tags?: string[] } | null> = {};
    for (const id of uniq) {
      const cur = ctx.crm.contactListPatches[id];
      snapshot[id] = cur ? { ...cur } : null;
    }
    const patch: Record<string, { ownerId?: string; tags?: string[] } | null> = {};
    for (const id of uniq) {
      const cur = ctx.crm.contactListPatches[id];
      patch[id] = { ...(cur ?? {}), ownerId };
    }
    ctx.mergeContactListPatches(patch);
    try {
      await Promise.resolve();
    } catch (e) {
      ctx.mergeContactListPatches(snapshot);
      throw e;
    }
  };

  const bulkTag = async (ids: string[], tag: string): Promise<void> => {
    const t = tag.trim();
    if (!t) return;
    stubLog('bulkTag', `${ids.length}:${t}`);
    const uniq = [...new Set(ids)];
    const snapshot: Record<string, { ownerId?: string; tags?: string[] } | null> = {};
    for (const id of uniq) {
      const cur = ctx.crm.contactListPatches[id];
      snapshot[id] = cur ? { ...cur } : null;
    }
    const patch: Record<string, { ownerId?: string; tags?: string[] } | null> = {};
    for (const id of uniq) {
      const prevTags = ctx.crm.contactListPatches[id]?.tags ?? [];
      const merged = [...new Set([...prevTags, t])];
      const cur = ctx.crm.contactListPatches[id];
      patch[id] = { ...(cur ?? {}), tags: merged };
    }
    ctx.mergeContactListPatches(patch);
    try {
      await Promise.resolve();
    } catch (e) {
      ctx.mergeContactListPatches(snapshot);
      throw e;
    }
  };

  const bulkDelete = async (entity: CrmEntityType, ids: string[]): Promise<void> => {
    const uniq = [...new Set(ids)];
    if (uniq.length === 0) return;
    stubLog('bulkDelete', `${entity}:${uniq.length}`);
    const n = uniq.length;
    const ok = await ctx.confirmDestructive({
      title: n === 1 ? 'Delete record?' : `Delete ${n} records?`,
      message:
        n === 1
          ? 'This record will be permanently removed. This cannot be undone.'
          : `These ${n} selected records will be permanently removed. This cannot be undone.`,
      confirmLabel: n === 1 ? 'Delete' : `Delete ${n}`,
    });
    if (!ok) return;
    if (entity === 'contact') {
      for (const id of uniq) ctx.removeContact(id);
      try {
        await Promise.all(uniq.map((id) => api.contacts.deleteContact(id)));
      } catch (e) {
        uniq.forEach((id) => rollback.restoreContact(id));
        throw e;
      }
      return;
    }
    if (entity === 'deal') {
      for (const id of uniq) ctx.removeOpportunity(id);
      try {
        await Promise.all(uniq.map((id) => api.deals.deleteRemote(id)));
      } catch (e) {
        uniq.forEach((id) => rollback.restoreOpportunity(id));
        throw e;
      }
    }
  };

  const bulkMoveStage = async (ids: string[], stage: OpportunityStage): Promise<void> => {
    stubLog('bulkMoveStage', `${ids.length}:${stage}`);
    const uniq = [...new Set(ids)];
    const snapshots = uniq.map((id) => {
      const row = ctx.crmOpportunities.find((o) => o.id === id);
      return { id, snap: row ? ({ ...row } as Opportunity) : null };
    });
    for (const { id } of snapshots) {
      ctx.patchOpportunity(id, { stage });
    }
    try {
      for (const { id } of snapshots) {
        await api.deals.persistOpportunityPatch({ id, stage });
      }
    } catch (e) {
      for (const { id, snap } of snapshots) {
        if (snap) ctx.patchOpportunity(id, snap);
      }
      throw e;
    }
  };

  /**
   * Applies each PATCH **sequentially** so concurrent optimistic merges do not race.
   * One failure does not roll back prior successes (caller clears selection per deal).
   *
   * Outcome counts match what **`Promise.allSettled`** would report per row (**`fulfilled`** /
   * **`rejected`**) so bulk advance toasts can show "X of Y … — Z failed" without running
   * parallel PATCHes (which would risk merge races in **`patchOpportunity`**).
   */
  const bulkPersistOpportunityPatchesSequential = async (
    items: { id: string; patch: Partial<Opportunity> }[]
  ): Promise<{ succeededIds: string[]; failedIds: string[] }> => {
    stubLog('bulkPersistOpportunityPatchesSequential', String(items.length));
    const succeededIds: string[] = [];
    const failedIds: string[] = [];
    for (const { id, patch } of items) {
      try {
        await persistOpportunityPatch({ id, ...patch });
        succeededIds.push(id);
      } catch {
        failedIds.push(id);
      }
    }
    return { succeededIds, failedIds };
  };

  return {
    openOpportunityDrawer,
    openCreateDeal,
    openCreateContact,
    openCreateAccount,
    openCreateTask: createTask,
    openCreateActivity,
    openScheduleFollowUp,
    applyFilters,
    resetFilters,
    exportRecords,
    exportRecordsToFile,
    deleteRecord,
    editRecord,
    viewRecord,
    persistOpportunityPatch,
    runPipelineGeneration,
    readOnlyFieldSaveAttempt,

    createTask,
    editTask,
    deleteTask,
    completeTask,
    logActivity,
    composeEmail,
    scheduleMeeting,
    logCall,
    createDeal,
    editDeal,
    deleteDeal,
    addContact,
    scheduleFollowUp,
    openDrilldown,
    saveSchedule,
    saveActivity,
    saveOpportunity,
    markActivityComplete,
    deleteActivity,
    goAccountsIndex,
    editTerritoryAccount,
    deleteTerritoryAccount,
    restoreTerritoryAccount,
    editCrmListContact,
    deleteCrmListContact,

    bulkAssign,
    bulkTag,
    bulkDelete,
    bulkMoveStage,
    bulkPersistOpportunityPatchesSequential,
  };
}

export type CrmActionsMap = {
  openOpportunityDrawer: (id: string) => void;
  openCreateDeal: () => void;
  openCreateContact: (prefill?: Record<string, string>) => void;
  openCreateAccount: () => void;
  openCreateTask: (prefill?: Partial<Task>) => void;
  openCreateActivity: (prefill?: Partial<Activity>) => void;
  openScheduleFollowUp: (entityId?: string, entityType?: Task['relatedEntityType']) => void;
  applyFilters: (filters: Record<string, unknown>) => void;
  resetFilters: () => void;
  exportRecords: {
    (format: 'csv' | 'xlsx'): void;
    (entityType: string, filters: CrmFilterState): void;
  };
  exportRecordsToFile: (format: 'csv' | 'xlsx', tabular?: CrmExportTabular) => void;
  deleteRecord: (type: string, id: string) => Promise<void>;
  editRecord: (type: string, id: string) => void;
  viewRecord: (type: string, id: string) => void;
  persistOpportunityPatch: (patch: Partial<Opportunity>) => Promise<void>;
  runPipelineGeneration: RunPipelineGenerationFn;
  readOnlyFieldSaveAttempt: (fieldLabel: string) => (value: string | number) => Promise<void>;

  createTask: (prefill?: Partial<Task>) => void;
  editTask: (id: string) => void;
  deleteTask: (id: string) => Promise<boolean>;
  completeTask: (id: string) => void;
  logActivity: (prefill?: ActivityModalOpenPrefill) => void;
  composeEmail: (contactId: string, prefill?: { subject?: string; body?: string }) => void;
  scheduleMeeting: (entityType: CrmEntityType, entityId: string) => void;
  logCall: ((entityType: CrmEntityType, entityId: string) => void) & ((payload: LogCallPayload) => Promise<void>);
  createDeal: (prefill?: Partial<Opportunity>) => void;
  editDeal: (id: string) => void;
  deleteDeal: (id: string) => Promise<void>;
  addContact: (prefill?: Record<string, string>) => void;
  scheduleFollowUp: (entityId: string, entityType: string) => void;
  openDrilldown: (config: DrilldownConfig) => void;
  saveSchedule: (data: Partial<Task> & Partial<Activity>) => Promise<void>;
  saveActivity: (data: Partial<Activity>) => Promise<void>;
  saveOpportunity: (data: Partial<Opportunity>) => Promise<Opportunity | undefined>;
  markActivityComplete: (activityId: string) => Promise<void>;
  deleteActivity: (activityId: string) => Promise<void>;
  goAccountsIndex: () => void;
  editTerritoryAccount: (accountId: string) => void;
  deleteTerritoryAccount: (accountId: string) => Promise<void>;
  restoreTerritoryAccount: (accountId: string) => Promise<void>;
  editCrmListContact: (contactId: string) => void;
  deleteCrmListContact: (
    contactId: string,
    opts?: { onCommitFailed?: (err: unknown) => void }
  ) => Promise<{ cancelCommit: () => void; restoreContact: () => void } | null>;

  bulkAssign: (ids: string[], ownerId: string) => Promise<void>;
  bulkTag: (ids: string[], tag: string) => Promise<void>;
  bulkDelete: (entity: CrmEntityType, ids: string[]) => Promise<void>;
  bulkMoveStage: (ids: string[], stage: OpportunityStage) => Promise<void>;
  bulkPersistOpportunityPatchesSequential: (
    items: { id: string; patch: Partial<Opportunity> }[]
  ) => Promise<{ succeededIds: string[]; failedIds: string[] }>;
};
